import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import firestoreService from '../../../services/firestoreService';
import styled from 'styled-components';
import useAuth from '../../../hooks/auth/useAuth';

const LectureFeedback = ({ nav, onNavChangeHandler }) => {
    const { user } = useAuth();
    const { id } = useParams();
    const [feedbackData, setFeedbackData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    // 피드백 데이터 로드
    useEffect(() => {
        const fetchFeedbackData = async () => {
            try {
                setLoading(true);
                setError(null);

                // 학생의 피드백 요청 데이터 가져오기
                const studentFeedbackDoc = await firestoreService.getDocument(
                    `LectureProgress/${id}/LectureFeedback`, 
                    user.uid
                );

                console.log(studentFeedbackDoc.data);

                if (studentFeedbackDoc.success && studentFeedbackDoc.data) {
                    setFeedbackData(studentFeedbackDoc.data);
                    if (studentFeedbackDoc.data.teacher_feedback && nav && !nav.completed) {
                        onNavChangeHandler({ ...nav, completed: true });
                    }
                }
            } catch (err) {
                console.error('피드백 데이터 로드 오류:', err);
                setError('피드백 데이터를 불러오는 중 오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        };

        if (id && user?.uid) {
            fetchFeedbackData();
        }
    }, [id, user?.uid]);


    const copyFeedback = async () => {
        if (!feedbackData?.teacher_feedback) return;
        try {
            await navigator.clipboard.writeText(feedbackData.teacher_feedback);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            setError('피드백을 복사하지 못했습니다.');
        }
    };

    const writtenAt = feedbackData?.updated_at?.seconds
        ? new Date(feedbackData.updated_at.seconds * 1000).toLocaleString('ko-KR')
        : '';

    if (loading || !user) {
        return <Container><Empty>피드백을 불러오는 중...</Empty></Container>;
    }

    if (error) {
        return <Container><Empty>{error}</Empty></Container>;
    }

    return (
        <Container>
            <Head>
                <div>
                    <Kicker>강사 첨삭</Kicker>
                    <Title>{feedbackData?.teacher_feedback ? '선생님 피드백이 도착했습니다' : '피드백을 기다리는 중'}</Title>
                </div>
                <Actions>
                    <Ghost type="button" onClick={() => window.location.reload()}>다시 확인</Ghost>
                    {feedbackData?.teacher_feedback ? (
                        <Ghost type="button" onClick={copyFeedback}>{copied ? '복사됨' : '복사'}</Ghost>
                    ) : null}
                    <Go to={`/lecture/${id}?step=5`}>리라이팅으로</Go>
                </Actions>
            </Head>

            {feedbackData?.teacher_feedback ? (
                <Paper>
                    <DetailContent>{feedbackData.teacher_feedback}</DetailContent>
                    {writtenAt ? <When>{writtenAt}</When> : null}
                </Paper>
            ) : (
                <Empty>
                    아직 선생님 피드백이 없습니다. AI 첨삭을 먼저 받고, 선생님이 확인하면 이곳에 표시됩니다.
                </Empty>
            )}
        </Container>
    );
};

// 스타일드 컴포넌트
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const Head = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-end;
    flex-wrap: wrap;
`;

const Kicker = styled.p`
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 600;
    color: #d9412b;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #1d2340;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const Ghost = styled.button`
    padding: 10px 12px;
    font-weight: 700;
    color: #1d2340;
    background: #fff;
    border: 1px solid #e7e1d3;
    border-radius: 6px;
    cursor: pointer;
`;

const Go = styled(Link)`
    padding: 10px 12px;
    font-weight: 700;
    color: #fff;
    background: #1d2340;
    border-radius: 6px;
    text-decoration: none;
    &:hover { text-decoration: none; background: #2c3561; }
`;

const Paper = styled.article`
    padding: 22px;
    background: #fffdf7;
    border: 1px solid #e7e1d3;
    border-radius: 4px;
    box-shadow: 6px 6px 0 #e9e2d0;
`;

const When = styled.p`
    margin: 14px 0 0;
    font-size: 12px;
    color: #6f6a5c;
`;

const Empty = styled.p`
    margin: 0;
    padding: 28px 20px;
    background: #fff;
    border: 1px dashed #e7e1d3;
    border-radius: 4px;
    color: #6f6a5c;
    line-height: 1.6;
`;

const DetailContent = styled.div`
    color: #1d2340;
    line-height: 1.8;
    white-space: pre-wrap;
    font-size: 15px;
    font-weight: 400;
    position: relative;
    z-index: 1;
`;


export default LectureFeedback;